import { AlertDialog as AlertPrimitive } from "radix-ui";
import type { ReactNode } from "react";
import { cn } from "@/lib/utils.ts";
import { Button } from "./button.tsx";

/** A confirmation for destructive actions (Delete), or for a choice the user should make knowingly. `children` go below the description. */
export function ConfirmDialog({
  open,
  onOpenChange,
  title,
  description,
  confirmLabel,
  cancelLabel,
  onConfirm,
  pending,
  destructive = true,
  children,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: ReactNode;
  description: ReactNode;
  confirmLabel: string;
  cancelLabel: string;
  onConfirm: () => void;
  pending?: boolean;
  destructive?: boolean;
  children?: ReactNode;
}) {
  return (
    <AlertPrimitive.Root open={open} onOpenChange={onOpenChange}>
      <AlertPrimitive.Portal>
        <AlertPrimitive.Overlay className="fixed inset-0 z-50 bg-[#0b1322]/45 backdrop-blur-[2px] data-[state=closed]:animate-fade-out data-[state=open]:animate-fade-in" />
        <AlertPrimitive.Content
          className={cn(
            "fixed top-1/2 left-1/2 z-50 flex w-[calc(100vw-2rem)] max-w-md -translate-x-1/2 -translate-y-1/2 flex-col gap-4 rounded-xl border bg-popover p-5 text-popover-foreground shadow-2xl shadow-black/15 data-[state=closed]:animate-pop-out data-[state=open]:animate-pop-in sm:p-6",
          )}
        >
          <div className="flex flex-col gap-1.5">
            <AlertPrimitive.Title className="text-base font-bold tracking-[-0.01em]">{title}</AlertPrimitive.Title>
            <AlertPrimitive.Description className="text-[13px] leading-relaxed text-muted-foreground">
              {description}
            </AlertPrimitive.Description>
          </div>
          {children}
          <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
            <AlertPrimitive.Cancel asChild>
              <Button variant="outline">{cancelLabel}</Button>
            </AlertPrimitive.Cancel>
            <Button
              variant={destructive ? "destructive" : "default"}
              disabled={pending}
              onClick={(event) => {
                event.preventDefault();
                onConfirm();
              }}
            >
              {confirmLabel}
            </Button>
          </div>
        </AlertPrimitive.Content>
      </AlertPrimitive.Portal>
    </AlertPrimitive.Root>
  );
}
